import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "@/pages/AdminDashboard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Image, Loader2, X } from "lucide-react";

export default function AdminPortfolio() {
  const { token } = useAuth();
  const [portfolio, setPortfolio] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    image_url: "",
    barber_id: "",
  });

  useEffect(() => {
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [portfolioRes, barbersRes] = await Promise.all([
        axios.get(`${API}/portfolio`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/barbers`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setPortfolio(portfolioRes.data);
      setBarbers(barbersRes.data);
    } catch (error) {
      // Portfolio might not exist yet
      try {
        const barbersRes = await axios.get(`${API}/barbers`, { headers: { Authorization: `Bearer ${token}` } });
        setBarbers(barbersRes.data);
      } catch (e) {}
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.image_url.trim()) {
      toast.error("Görsel URL'si gerekli");
      return;
    }
    setFormLoading(true);
    try {
      await axios.post(`${API}/portfolio`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Referans eklendi");
      setForm({ title: "", description: "", image_url: "", barber_id: "" });
      setShowForm(false);
      fetchData();
    } catch (error) {
      toast.error("Eklenemedi");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/portfolio/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Referans silindi");
      setPortfolio(prev => prev.filter(p => p.id !== id));
    } catch (error) {
      toast.error("Silinemedi");
    }
  };

  return (
    <AdminLayout title="Referanslar">
      <div>
        <div className="flex items-center justify-between mb-6">
          <p className="text-[#6b7280]">Kesim ve bakım çalışmalarınızı ekleyin</p>
          <Button onClick={() => setShowForm(!showForm)} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Ekle
          </Button>
        </div>

        {/* Add Form */}
        {showForm && (
          <div className="bg-white border border-[#e5e7eb] rounded-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#1a1a2e]">Yeni Referans Ekle</h3>
              <button onClick={() => setShowForm(false)} className="text-[#9ca3af] hover:text-[#6b7280]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Başlık</label>
                <Input
                  placeholder="Modern Saç Kesimi"
                  value={form.title}
                  onChange={(e) => setForm(prev => ({ ...prev, title: e.target.value }))}
                  className="input-elite"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Berber</label>
                <select
                  value={form.barber_id}
                  onChange={(e) => setForm(prev => ({ ...prev, barber_id: e.target.value }))}
                  className="input-elite w-full h-10"
                >
                  <option value="">Berber seçin</option>
                  {barbers.map(b => (
                    <option key={b.id} value={b.id}>{b.name}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Görsel URL</label>
                <Input
                  placeholder="https://example.com/image.jpg"
                  value={form.image_url}
                  onChange={(e) => setForm(prev => ({ ...prev, image_url: e.target.value }))}
                  className="input-elite"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium text-[#6b7280] mb-1.5 block">Açıklama</label>
                <Input
                  placeholder="Kısa açıklama"
                  value={form.description}
                  onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
                  className="input-elite"
                />
              </div>
              <div className="md:col-span-2">
                <Button type="submit" className="btn-primary" disabled={formLoading}>
                  {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
                  Ekle
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Portfolio Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : portfolio.length === 0 ? (
          <div className="bg-white border border-[#e5e7eb] rounded-xl p-12 text-center">
            <Image className="w-12 h-12 text-[#d1d5db] mx-auto mb-4" />
            <p className="text-[#6b7280]">Henüz referans eklenmemiş</p>
            <p className="text-[#9ca3af] text-sm mt-1">Yukarıdaki butona tıklayarak ilk referansınızı ekleyin</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {portfolio.map((item) => (
              <div key={item.id} className="bg-white border border-[#e5e7eb] rounded-xl overflow-hidden group">
                <div className="aspect-square relative">
                  <img
                    src={item.image_url}
                    alt={item.title || "Referans"}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="absolute top-3 right-3 w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="p-4">
                  <p className="font-semibold text-[#1a1a2e] text-sm">{item.title || "Referans"}</p>
                  {item.barber_name && <p className="text-[#6b7280] text-xs mt-1">{item.barber_name}</p>}
                  {item.description && <p className="text-[#9ca3af] text-xs mt-1">{item.description}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
